import { useState, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';

import { cn, POPOVER_MOTION } from '../../shared/lib/cn';
import { isCustomLayout, useTableLayout } from '../../shared/lib/use-table-layout';
import { Button } from '../../shared/ui/button';
import { GROUP_LABEL } from '../../shared/ui/group-label';
import { Hint } from '../../shared/ui/hint';
import { CloseIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import { InfoDot } from '../../shared/ui/tooltip';
import { Switch } from '../../shared/ui/switch';

const EVENT_INFO =
  'Every column here exists on every event, whoever sent it. Anything that only one sender ' +
  'carries stays out of the table — payload paths differ from platform to platform, so a ' +
  'column built from one would be empty on most rows. They are reachable through the search ' +
  'field instead.';

// A path, not a query: letters, digits and `_ - $` between dots, as the scanner walks it.
const PATH = /^[\w$-]+(\.[\w$-]+)*$/;

interface IPayloadPaths {
  paths: readonly string[];
  onChange: (next: string[]) => void;
  max: number;
}

const PayloadPaths = ({ paths, onChange, max }: IPayloadPaths) => {
  const [draft, setDraft] = useState('');
  const path = draft.trim();
  const valid = PATH.test(path) && !paths.includes(path);

  return (
    <div className="mt-3 flex flex-col gap-inset border-t border-border-raised pt-3">
      <span className={cn(GROUP_LABEL, 'px-inset')}>Payload paths</span>
      {paths.map((each) => (
        <span
          key={each}
          className="flex h-row items-center gap-inset rounded-item px-inset font-mono text-data text-primary"
        >
          <span className="min-w-0 truncate">{each}</span>
          <button
            type="button"
            aria-label={`Remove ${each}`}
            onClick={() => onChange(paths.filter((one) => one !== each))}
            className="ml-auto text-faint hover:text-primary"
          >
            <CloseIcon size={12} />
          </button>
        </span>
      ))}
      {paths.length < max ? (
        <form
          className="flex gap-inset"
          onSubmit={(event) => {
            event.preventDefault();
            if (!valid) return;
            onChange([...paths, path]);
            setDraft('');
          }}
        >
          <Input
            size="sm"
            mono
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="data.object.status"
            aria-label="Payload path to add as a column"
            className="min-w-0 flex-1"
          />
          <Button type="submit" size="sm" variant="secondary" disabled={!valid}>
            Add
          </Button>
        </form>
      ) : (
        <Hint>{max} paths at most — each one reads every body on the page.</Hint>
      )}
    </div>
  );
};

interface IColumnsPanelProps<K extends string> {
  columns: readonly K[];
  onChange: (next: K[]) => void;
  sourceScoped?: boolean;
  options: readonly { key: K; label: string }[];
  info?: ReactNode;
  payload?: IPayloadPaths;
  /** The table whose dragged widths and order "Reset columns" clears. */
  layoutId?: string;
}

export const ColumnsPanel = <K extends string>({
  columns,
  onChange,
  sourceScoped,
  options,
  info,
  payload,
  layoutId,
}: IColumnsPanelProps<K>) => {
  const [open, setOpen] = useState(false);
  const { layout, reset } = useTableLayout(layoutId);
  const picked = new Set<string>(columns);
  const choices: readonly { key: string; label: string }[] = options;
  const keys = options.map((option) => option.key);

  // Always in the options' own order, whatever order the switches were flipped in.
  const toggle = (key: string, on: boolean) => {
    const next = new Set(picked);
    if (on) next.add(key);
    else next.delete(key);
    onChange(keys.filter((each) => next.has(each)) as K[]);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button variant="ghost" size="sm">
          Columns
        </Button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          // Radix focuses the first focusable child on open, and that is the
          // "i" button — whose tooltip opens on focus, so the panel appeared
          // with its explanation already covering the switches.
          onOpenAutoFocus={(event) => event.preventDefault()}
          className={cn(
            'w-panel-sm rounded-card border border-border-raised bg-surface-raised p-3',
            'origin-(--radix-popover-content-transform-origin)',
            POPOVER_MOTION,
          )}
        >
          <div className="mb-inset flex items-center gap-inset px-inset">
            <span className={GROUP_LABEL}>Columns</span>
            <InfoDot side="bottom">{info ?? EVENT_INFO}</InfoDot>
          </div>

          <div className="flex flex-col">
            {choices.map((column) => {
              const on = picked.has(column.key);
              const locked = on && picked.size === 1;
              return (
                <label
                  key={column.key}
                  className={cn(
                    'flex h-row items-center gap-inset rounded-item px-inset text-data',
                    locked ? 'text-faint' : 'cursor-pointer text-primary hover:bg-hover-raised',
                  )}
                >
                  <span>{column.label}</span>
                  <Switch
                    size="sm"
                    className="ml-auto"
                    checked={on}
                    disabled={locked}
                    aria-label={column.label}
                    onCheckedChange={(next) => toggle(column.key, next)}
                  />
                </label>
              );
            })}
          </div>

          {payload && <PayloadPaths {...payload} />}

          {sourceScoped && picked.has('source') && (
            <Hint className="mt-3">
              This view is already narrowed to one source, so the Source column repeats the filter —
              turning it off costs you nothing.
            </Hint>
          )}

          {layoutId && (
            <div className="mt-3 flex items-center gap-inset border-t border-border-raised px-inset pt-3">
              <span className="text-meta text-muted">Widths and order you dragged</span>
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto"
                disabled={!isCustomLayout(layout)}
                onClick={reset}
              >
                Reset columns
              </Button>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
